import React from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';
import ParcelFilterStyle from '../../../../delivery/components/ParcelFilterStyle';
import DateFilterItem from '../../../../delivery/components/DateFilterItem';
import { ViewMode } from './TaskMonitoringFilter';

export interface TaskMonitoringFilters {
    taskDate: dayjs.Dayjs;
    selectedWarehouses?: string[];
    selectedCities: string[];
    selectedDsps: string[];
    selectedAreas: string[];
    /** 司机 accountId 字符串数组（不是姓名，姓名允许重名，见 utils/driverFilterOption） */
    selectedDrivers: string[];
    viewMode: ViewMode;
}

interface HubTaskMonitoringFilterProps {
    filters: TaskMonitoringFilters;
    onFiltersChange: (filters: TaskMonitoringFilters) => void;
    warehouses: Array<{ id: string; name: string }>; // hub 结构关联的仓库数据
    dsps: Array<{ id: number; name: string }>; // hub 结构关联的DSP数据
    showResetButton?: boolean;
}

const HubTaskMonitoringFilter: React.FC<HubTaskMonitoringFilterProps> = ({
    filters,
    onFiltersChange,
    warehouses,
    dsps,
    showResetButton = true
}) => {
    const { t } = useTranslation();

    // DSP列表现在由后端根据hubId过滤，这里直接使用传入的dsps
    const filteredDsps = dsps;

    const labelTaskDate = String(t('wms.delivery.dailyTaskMonitoring.buttons.taskDate'));
    const labelWhs = String(t('wms.delivery.dailyTaskMonitoring.buttons.whs'));
    const labelDsp = String(t('wms.delivery.dailyTaskMonitoring.buttons.dsp'));

    // Hub页面总是显示：时间、WHS、DSP
    const col1Labels = [labelTaskDate];
    const col2Labels = [labelWhs];
    const col3Labels = [labelDsp];

    // 计算最近30天的日期范围
    const today = dayjs();
    const thirtyDaysAgo = today.subtract(29, 'day');

    // 禁用不在最近30天范围内的日期
    const disabledDate = (current: dayjs.Dayjs | null) => {
        if (!current) return false;
        return current.isBefore(thirtyDaysAgo, 'day') || current.isAfter(today, 'day');
    };

    const handleReset = () => {
        onFiltersChange({
            ...filters,
            taskDate: dayjs(),
            selectedWarehouses: [],
            selectedDsps: [],
            selectedAreas: [],
            selectedDrivers: [],
            selectedCities: []
        });
    };

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        {/* 任务日期 */}
                        <div className="min-w-0">
                            <DateFilterItem
                                label={labelTaskDate}
                                value={filters.taskDate}
                                onChange={(date) => onFiltersChange({...filters, taskDate: date || dayjs()})}
                                disabledDate={disabledDate}
                                spanClassName={getSmartLabelClassName(col1Labels)}
                            />
                        </div>
                        {/* WHS 下拉 */}
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={labelWhs}
                                options={warehouses.map(whs => ({
                                    value: String(whs.id),
                                    label: String(whs.name ?? whs.id)
                                }))}
                                value={filters.selectedWarehouses || []}
                                onChange={(value: string[]) => onFiltersChange({ ...filters, selectedWarehouses: value })}
                                className="w-full"
                                spanClassName={getSmartLabelClassName(col2Labels)}
                                mode="multiple"
                                showSearch
                                placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.whs') || undefined}
                                maxTagCount="responsive"
                            />
                        </div>
                        {/* DSP 下拉 */}
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={labelDsp}
                                options={filteredDsps.map(dsp => ({ value: dsp.name, label: dsp.name }))}
                                value={filters.selectedDsps}
                                onChange={(value: string[]) => onFiltersChange({...filters, selectedDsps: value})}
                                className="w-full"
                                spanClassName={getSmartLabelClassName(col3Labels)}
                                mode="multiple"
                                showSearch
                                placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.dsp') || undefined}
                            />
                        </div>
                    </div>
                </div>

                {/* 重置按钮 */}
                {showResetButton && (
                    <div className="flex-shrink-0">
                        <Button onClick={handleReset}>
                            {t('wms.delivery.dailyTaskMonitoring.buttons.reset')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HubTaskMonitoringFilter;